import React from 'react';
import { PillCell } from '@/components/atoms';
import { Text } from '@/components/atoms/AppText';
import type { PillPreset } from '@/components/organism/StatusPills/pillCell.types';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { APP_BG } from '@/constant/theme/colors.constant';

export interface FeatureFlagCardProps {
  label: string;
  /** Short description shown beneath the label */
  desc: string;
  enabled?: boolean;
  /** SVG icon rendered inside the icon tile */
  icon: React.ReactNode;
  /** Label shown in the status pill when enabled. Default: "Enabled" */
  enabledLabel?: string;
  /** Label shown in the status pill when disabled. Default: "Disabled" */
  disabledLabel?: string;
  /**
   * PillCell preset used for the status pill.
   * Default: "statusEnabled"
   */
  statusPreset?: PillPreset;
  /**
   * Tailwind classes applied to the card border when enabled.
   * Default: "border-orange-200/80 dark:border-orange-900/40"
   */
  enabledBorderClass?: string;
}

/**
 * FeatureFlagCard — divide-x grid card for a single feature flag.
 * Left cell: icon + label + description.  Right cell: StatusPill.
 */
const FeatureFlagCard = ({
  label,
  desc,
  enabled,
  icon,
  enabledLabel = ORGANISM_PROPS.FEATURE_FLAG_CARD.ENABLED,
  disabledLabel = ORGANISM_PROPS.FEATURE_FLAG_CARD.DISABLED,
  statusPreset = 'statusEnabled',
  enabledBorderClass = 'border-orange-200/80 dark:border-orange-900/40',
}: FeatureFlagCardProps) => (
  <div
    className={`rounded-xl border overflow-hidden ${
      enabled
        ? `${enabledBorderClass} shadow-sm`
        : 'border-gray-200 dark:border-gray-700'
    }`}
  >
    <div
      className={`grid grid-cols-[1fr_auto] divide-x divide-gray-100 dark:divide-gray-700/60 ${APP_BG.base}`}
    >
      {/* Label + description */}
      <div className="flex items-start gap-3 px-4 py-4">
        <div
          className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
            enabled
              ? 'bg-gradient-to-br from-primary to-secondary text-white shadow-sm'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
          }`}
        >
          {icon}
        </div>
        <div className="flex flex-col gap-0.5 min-w-0">
          <Text as="p" size="xl" weight="bold" color="default">
            {label}
          </Text>
          <Text
            as="p"
            size="base"
            color="muted"
            leading="relaxed"
            className="mt-0.5"
          >
            {desc}
          </Text>
        </div>
      </div>

      {/* Status cell */}
      <div
        className={`flex items-center justify-end sm:justify-center px-4 sm:px-5 py-3 sm:py-0 ${APP_BG.overlay}`}
      >
        <PillCell
          value={enabled ? enabledLabel : disabledLabel}
          preset={statusPreset}
          dot
          size="text-xs"
          customStyle={{ padding: '0.25rem 0.625rem' }}
        />
      </div>
    </div>
  </div>
);

export default FeatureFlagCard;
